import axios from 'axios';
import { unwrapListResponse, unwrapSingleResponse } from '../apiUnwrapper/apiUnwrapper';
import { User, UserRequest, Post, PostRequest, EquipmentType, Operator, Material, LoadLocation, LoadLocationForm, DumpLocation, ReportDashboard, ReportFilterOptions, DashboardRequest } from '../types';

export const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8081/api').replace(/\/+$/, '');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    console.log(`Making ${config.method?.toUpperCase()} request to ${config.url}`);
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    // ❗ Do NOT refresh tokens when /auth/me fails
    if (originalRequest.url.includes('/auth/me')) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        await api.post('/auth/refresh');
        return api(originalRequest);
      } catch (refreshError) {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// helper to append paging params to path (handles existing query string)
function withPaging(path: string, page = 1, size = 100) {
  const paging = `page=${page}&size=${size}`;
  return path.includes('?') ? `${path}&${paging}` : `${path}?${paging}`;
}


//Equipment API
export const equipmentApi = {
  getEquipments: (page = 1, size = 100): Promise<any[]> =>
    api.get(withPaging('/v1/equipment', page, size)).then(res => res.data),

  getEquipmentsForSearchTerm: (searchTerm: string, page = 1, size = 100) =>
  api.get(withPaging(`/v1/equipment/search?searchTerm=${searchTerm}`, page, size)).then(res => res.data),

  getEquipmentTypes: (page = 1, size = 100): Promise<any[]> =>
    api.get(withPaging('/v1/equipment/types', page, size)).then(res => res.data),

  getEquipmentTypesForSearchTerm: (searchTerm: string, page = 1, size = 100) =>
  api.get(withPaging(`/v1/equipment/types/search?searchTerm=${searchTerm}`, page, size)).then(res => res.data),

  getEquipmentDefinitions: (page = 1, size = 100): Promise<any[]> =>
    api.get(withPaging('/v1/equipment/definition', page, size)).then(res => res.data),

  getEquipmentDefinitionsBasic: (page = 1, size = 100): Promise<any[]> =>
    api.get(withPaging('/v1/equipment/definition/listbasic', page, size)).then(res => res.data),

  getEquipmentDefinitionsForSearchTerm: (searchTerm: string, page = 1, size = 100) =>
  api.get(withPaging(`/v1/equipment/definition/search?searchTerm=${searchTerm}`, page, size)).then(res => res.data),

  createEquipment: (equipmentUnit: any): Promise<any> =>
    api.post('/v1/equipment', equipmentUnit).then(res => res.data),

  updateEquipment: (equipmentUnit: any): Promise<any> =>
    api.put(`/v1/equipment/`, equipmentUnit).then(res => res.data),

  deleteEquipment: (unitId: number): Promise<void> =>
    api.delete(`/v1/equipment/${unitId}`).then(() =>{}),

  addEquipmentType: (type: EquipmentType): Promise<EquipmentType | null> =>
    api.post('/v1/equipment/types', type).then(res => unwrapSingleResponse(res.data)),
  
  deleteEquipmentType: (id: number): Promise<void> =>
    api.delete(`/v1/equipment/types/${id}`).then(() => {}),

  addEquipmentDefinition: (def: any): Promise<any> =>
    api.post('/v1/equipment/definition', def).then(res => unwrapSingleResponse(res.data)),

  updateEquipmentDefinition: (def: any): Promise<any> =>
    api.put('/v1/equipment/definition', def).then(res => unwrapSingleResponse(res.data)),

  deleteEquipmentDefinition: (id: number): Promise<void> =>
    api.delete(`/v1/equipment/definition/${id}`).then(() => {}),

  getQuestions: (page = 1, size = 100): Promise<any[]> =>
    api.get(withPaging('/v1/equipment/questions', page, size)).then(res => res.data),

  createUpdateQuestion: (question: any): Promise<any> =>
    api.post('/v1/equipment/questions', question).then(res => unwrapSingleResponse(res.data)),

  deleteQuestion: (id: number): Promise<void> =>
    api.delete(`/v1/equipment/questions/${id}`).then(() => {}),

  getBreakdownCategories: (page = 1, size = 100): Promise<any[]> =>
    api.get(withPaging('/v1/equipment/breakdown/category', page, size)).then(res => res.data),

  createUpdateBreakdownCategory: (question: any): Promise<any> =>
    api.post('/v1/equipment/breakdown/category', question).then(res => unwrapSingleResponse(res.data)),

  deleteBreakdownCategory: (id: number): Promise<void> =>
    api.delete(`/v1/equipment/breakdown/category/${id}`).then(() => {}),

  getBreakdownSeverity: (page = 1, size = 100): Promise<any[]> =>
    api.get(withPaging('/v1/equipment/breakdown/severity', page, size)).then(res => res.data),

  createUpdateBreakdownSeverity: (question: any): Promise<any> =>
    api.post('/v1/equipment/breakdown/severity', question).then(res => unwrapSingleResponse(res.data)),

  deleteBreakdownSeverity: (id: number): Promise<void> =>
    api.delete(`/v1/equipment/breakdown/severity/${id}`).then(() => {}),

  bulkUploadEquipments: async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await api.post("/v1/equipment/bulkupload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      return res.data; // success
    } catch (err: any) {
      // Axios puts backend JSON here for 400/500 responses
      if (err.response && err.response.data) {
        return err.response.data; // return error JSON to UI
      }

      // Network or unexpected error
      throw err;
    }
  },

  downloadTemplate: (): Promise<void> =>
  api.get("/v1/equipment/bulkupload/template", {
    responseType: "blob" // important for file downloads
  }).then(res => {
    const contentType = res.headers["content-type"]?.toString();
    const blob = new Blob([res.data], { type: contentType });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "equipment-template.xlsx"; // filename
    a.click();

    window.URL.revokeObjectURL(url);
  })
};

//Shift API
export const shiftApi = {
  getShifts: (page = 1, size = 100): Promise<any[]> =>
    api.get(withPaging('/v1/shift', page, size)).then(res => unwrapListResponse(res.data)),

  createUpdateShift: (shift: any): Promise<any> =>
    api.post('/v1/shift', shift).then(res => unwrapSingleResponse(res.data)),

  deleteShift: (shiftId: number): Promise<void> =>
    api.delete(`/v1/shift/${shiftId}`).then(() => {}),
}

//Crew API
export const crewApi = {
  getCrew: (page = 1, size = 100): Promise<any[]> =>
    api.get(withPaging('/v1/crew', page, size)).then(res => res.data),

  getCrewForSearchTerm: (searchTerm: string, page = 1, size = 100) =>
    api.get(withPaging(`/v1/crew/search?searchTerm=${searchTerm}`, page, size)).then(res => res.data),

  createUpdateCrew: (crew: any): Promise<any> =>
    api.post('/v1/crew', crew).then(res => res.data),

  deleteCrew: (crewId: number): Promise<void> =>
    api.delete(`/v1/crew/${crewId}`).then(() => {}),
}

//Operator API
export const operatorApi = {
  getOperators: (page = 1, size = 100): Promise<any[]> =>
    api.get(withPaging('/v1/operator', page, size)).then(res => res.data),

  getAllOperatorsForSearchTerm: (searchTerm: string, page = 1, size = 100) =>
  api.get(withPaging(`/v1/operator/search?searchTerm=${searchTerm}`, page, size)).then(res => res.data),


  createOperator: (operator: Operator): Promise<any> =>
    api.post('/v1/operator', operator).then(res => res.data),

  updateOperator: (operator: Operator): Promise<any> =>
    api.put('/v1/operator', operator).then(res => res.data),

  deleteOperator: (operatorId: number): Promise<void> =>
    api.delete(`/v1/operator/${operatorId}`).then(() => {}),

  bulkUploadOperators: async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await api.post("/v1/operator/bulkupload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      return res; // success
    } catch (err: any) {
      // Axios puts backend JSON here for 400/500 responses
      if (err.response && err.response.data) {
        return err.response.data; // return error JSON to UI
      }

      // Network or unexpected error
      throw err;
    }
  },

  downloadTemplate: (): Promise<void> =>
  api.get("/v1/operator/bulkupload/template", {
    responseType: "blob" // important for file downloads
  }).then(res => {
    const contentType = res.headers["content-type"]?.toString();
    const blob = new Blob([res.data], { type: contentType });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "operator-template.xlsx"; // filename
    a.click();

    window.URL.revokeObjectURL(url);
  })
}

//FLHA API
export const flhaApi = {
  getQuestions: (page = 1, size = 100): Promise<any[]> =>
    api.get(withPaging('/v1/flha', page, size)).then(res => res.data),

  createUpdateQuestion: (question: any): Promise<any> =>
    api.post('/v1/flha', question).then(res => unwrapSingleResponse(res.data)),

  deleteQuestion: (id: number): Promise<void> =>
    api.delete(`/v1/flha/${id}`).then(() => {}),
};

//Fuel
export const fuelApi = {
  getFuelTypes: (page = 1, size = 100): Promise<any[]> =>
    api.get(withPaging('/v1/fuel/types', page, size)).then(res => res.data),

  getFuelSources: (page = 1, size = 100): Promise<any[]> =>
    api.get(withPaging('/v1/fuel/sources', page, size)).then(res => res.data),

  createUpdateFuelType: (fuelType: any): Promise<any> =>
    api.post('/v1/fuel/types', fuelType).then(res => unwrapSingleResponse(res.data)),

  createUpdateFuelSource: (fuelSource: any): Promise<any> =>
    api.post('/v1/fuel/sources', fuelSource).then(res => unwrapSingleResponse(res.data)),

  deleteFuelType: (id: number): Promise<void> =>
    api.delete(`/v1/fuel/types/${id}`).then(() => {}),
  
  deleteFuelSource: (id: number): Promise<void> =>
    api.delete(`/v1/fuel/sources/${id}`).then(() => {}),
};

//Non Productive Time(NPT) reasons
export const nptApi = {
  getAllNPTReasons: (page = 1, size = 100): Promise<any[]> =>
    api.get(withPaging('/v1/npt', page, size)).then(res => (res.data)),

  createUpdateNPTReason: (nptReason: any): Promise<any> =>
    api.post('/v1/npt', nptReason).then(res => unwrapSingleResponse(res.data)),

  deleteNPTResponse: (id: number): Promise<void> =>
    api.delete(`/v1/npt/${id}`).then(() => {}),
};

//Material API
export const materialApi = {
  getMaterials: (page = 1, size = 100): Promise<any[]> =>
    api.get(withPaging('/v1/materials', page, size)).then(res => res.data),

  geActivetMaterials: (): Promise<any[]> =>
    api.get('/v1/materials/active?include_active=true').then(res => res.data),

  createUpdateMaterial: (material: Material): Promise<any> =>
    api.post('/v1/materials', material).then(res => res.data),

  deleteMaterial: (id: number): Promise<void> =>
    api.delete(`/v1/materials/${id}`).then(() => {}),
};

//Load Location API
export const loadLocationApi = {
  getLoadLocations: (page = 1, size = 100): Promise<any[]> =>
    api.get(withPaging('/v1/loadlocations', page, size)).then(res => res.data),

  createLoadLocation: (loadLocation: LoadLocationForm): Promise<any> =>
    api.post('/v1/loadlocations', loadLocation).then(res => res.data),

  updateLoadLocation: (loadLocation: LoadLocationForm): Promise<any> =>
    api.put('/v1/loadlocations', loadLocation).then(res => res.data),

  deleteLoadLocation: (id: number): Promise<void> =>
    api.delete(`/v1/loadlocations/${id}`).then(() => {}),
};

//Load Location API
export const dumpLocationApi = {
  getDumpLocations: (page = 1, size = 100): Promise<any[]> =>
    api.get(withPaging('/v1/dumplocations', page, size)).then(res => res.data),

  createDumpLocation: (dumpLocation: DumpLocation): Promise<any> =>
    api.post('/v1/dumplocations', dumpLocation).then(res => res.data),

  updateDumpLocation: (dumpLocation: DumpLocation): Promise<any> =>
    api.put('/v1/dumplocations', dumpLocation).then(res => res.data),

  deleteDumpLocation: (id: number): Promise<void> =>
    api.delete(`/v1/dumplocations/${id}`).then(() => {}),
};

//dashboard api
export const dashboardApi = {
  getDashboardMetrics: (): Promise<any[]> =>
    api.get('/v1/dashboard').then(res => res.data),
};

// ------------------------------
// Production Plan Upload APIs
// ------------------------------
export const planApi = {
  uploadPlan: async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await api.post("/v1/plan/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      return res.data; // success
    } catch (err: any) {
      // Axios puts backend JSON here for 400/500 responses
      if (err.response && err.response.data) {
        return err.response.data; // return error JSON to UI
      }

      // Network or unexpected error
      throw err;
    }
  },

  listPlans: (): Promise<any[]> =>
    api.get("/v1/plan/list").then(res => res.data),

  getPlanStatusHistory: (planId: number): Promise<any[]> =>
    api.get(`/v1/plan/status/${planId}`).then(res => res.data),

  downloadTemplate: (): Promise<void> =>
  api.get("/v1/plan/template", {
    responseType: "blob" // important for file downloads
  }).then(res => {
    const contentType = res.headers["content-type"]?.toString();
    const blob = new Blob([res.data], { type: contentType });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "production-plan-template.xlsx"; // filename
    a.click();

    window.URL.revokeObjectURL(url);
  })
};

export const dispatchApi = {
  uploadDispatch: async (file: File, fileName: string): Promise<any> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("fileName", fileName);

    try {
      const res = await api.post("/v1/dispatch/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      return res.data; // success
    } catch (err: any) {
      // Axios puts backend JSON here for 400/500 responses
      if (err.response && err.response.data) {
        return err.response.data; // return error JSON to UI
      }

      // Network or unexpected error
      throw err;
    }
  },

  listDispatches: (): Promise<any[]> =>
    api.get("/v1/dispatch/list").then(res => res.data),

  downloadTemplate: (): Promise<void> =>
  api.get("/v1/dispatch/template", {
    responseType: "blob" // important for file downloads
  }).then(res => {
    const contentType = res.headers["content-type"]?.toString();
    const blob = new Blob([res.data], { type: contentType });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "dispatch-template.xlsx"; // filename
    a.click();

    window.URL.revokeObjectURL(url);
  })
};


export const documentsApi = {
  uploadDocument: async (formData: FormData): Promise<any> => {
    try {
      const res = await api.post("/v1/documents/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      return res.data; // success
    } catch (err: any) {
      // Axios puts backend JSON here for 400/500 responses
      if (err.response && err.response.data) {
        return err.response.data; // return error JSON to UI
      }

      // Network or unexpected error
      throw err;
    }
  },

  listDocuments: (): Promise<any[]> =>
    api.get("/v1/documents/list").then(res => res.data?.data),
};


// User API
export const userApi = {
  getUsers: (page = 1, size = 100): Promise<User[]> => 
    api.get(withPaging('/v1/users', page, size)).then(res => unwrapListResponse(res.data)),
  
  getUserById: (id: number): Promise<User | null> => 
    api.get(`/v1/users/${id}`).then(res => unwrapSingleResponse(res.data)),
  
  createUser: (user: UserRequest): Promise<User | null> => 
    api.post('/v1/users', user).then(res => unwrapSingleResponse(res.data)),
  
  updateUser: (id: number, user: UserRequest): Promise<User | null> => 
    api.put(`/v1/users/${id}`, user).then(res => unwrapSingleResponse(res.data)),
  
  deleteUser: (id: number): Promise<void> => 
    api.delete(`/v1/users/${id}`).then(() => {}),
  
  searchUsers: (name: string, page = 1, size = 100): Promise<User[]> => 
    api.get(withPaging(`/v1/users/search?name=${encodeURIComponent(name)}`, page, size)).then(res => unwrapListResponse(res.data)),
};

// Post API
export const postApi = {
  getPosts: (page = 1, size = 100): Promise<Post[]> => 
    api.get(withPaging('/v1/posts', page, size)).then(res => unwrapListResponse(res.data)),
  
  getPostById: (id: number): Promise<Post | null> => 
    api.get(`/v1/posts/${id}`).then(res => unwrapSingleResponse(res.data)),
  
  getPostsByUserId: (userId: number, page = 1, size = 100): Promise<Post[]> => 
    api.get(withPaging(`/v1/posts/user/${userId}`, page, size)).then(res => unwrapListResponse(res.data)),
  
  createPost: (post: PostRequest): Promise<Post | null> => 
    api.post('/v1/posts', post).then(res => unwrapSingleResponse(res.data)),
  
  updatePost: (id: number, post: PostRequest): Promise<Post | null> => 
    api.put(`/v1/posts/${id}`, post).then(res => unwrapSingleResponse(res.data)),
  
  deletePost: (id: number): Promise<void> => 
    api.delete(`/v1/posts/${id}`).then(() => {}),
  
  searchPosts: (query: string, page = 1, size = 100): Promise<Post[]> => 
    api.get(withPaging(`/v1/posts/search?q=${encodeURIComponent(query)}`, page, size)).then(res => unwrapListResponse(res.data)),
  
  getLatestPosts: (page = 1, size = 100): Promise<Post[]> => 
    api.get(withPaging('/v1/posts/latest', page, size)).then(res => unwrapListResponse(res.data)),
};

// External API
export const externalApi = {
  getExternalPosts: (page = 1, size = 100): Promise<any[]> => 
    api.get(withPaging('/v1/external/posts', page, size)).then(res => unwrapListResponse(res.data)),
  
  getExternalUsers: (page = 1, size = 100): Promise<any[]> => 
    api.get(withPaging('/v1/external/users', page, size)).then(res => unwrapListResponse(res.data)),
};

// External API Service for third-party integration
export const externalApiService = {
  getPosts: () => 
    axios.get('https://jsonplaceholder.typicode.com/posts'),
  
  getUsers: () => 
    axios.get('https://jsonplaceholder.typicode.com/users'),
  
  getComments: () => 
    axios.get('https://jsonplaceholder.typicode.com/comments'),
  
  getCustomData: (url: string) => 
    axios.get(url),
};

// App Info API
export const appApi = {
  getInfo: (): Promise<any> =>
    api.get('/info').then(res => unwrapSingleResponse(res.data)),
  
  getHealth: (): Promise<any> =>
    api.get('/health').then(res => unwrapSingleResponse(res.data)),
};

export const aiApi = {
  chat: (message: string): Promise<string> =>
    api.post('/v1/lambda/invoke', { message }).then(res => res.data?.response ?? res.data),
};

//Reports
// export const reportsApi = {
//   getFilterOptions: (): Promise<ReportFilterOptions> =>
//     api.get('/v1/reports/filter-options').then(res => res.data),

//   getDashboard: (req: DashboardRequest): Promise<ReportDashboard> =>
//     api.post('/v1/reports/dashboard', req).then(res => res.data),
// };

// Add to src/services/api.ts (use the same `api` axios instance and path prefix your existing reportsApi uses).
export const reportDashboardApi = {
  options: (): Promise<any> => api.get('/v1/reports/dashboard/options').then((r) => r.data),
  
  // section: compass | production | equipment | events | fuel | safety | voice
  section: (name: string, req: any): Promise<any> =>
    api.post(`/v1/reports/dashboard/${name}`, req).then((r) => r.data),
};

export default api;